import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Box, Button, Container, ContentLayout, Header,
    Select, SegmentedControl, SpaceBetween, Spinner,
} from '@cloudscape-design/components';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { alphaPicksApi } from '../../client/clients';
import {
    AlphaPickBarDto, AlphaPickSummary, SimulationResponse,
    GetDailyBarsEntryTypeEnum, GetSimulationEntryTypeEnum, GetSimulationStrategyEnum,
} from '../../api';
import { createChart, CandlestickSeries, AreaSeries, ColorType } from 'lightweight-charts';
import type { IChartApi, Time } from 'lightweight-charts';

const CHART_LAYOUT = {
    layout: {
        background: { type: ColorType.Solid, color: '#0d0d1f' },
        textColor: '#9fa8da',
    },
    grid: {
        vertLines: { color: '#1a237e' },
        horzLines: { color: '#1a237e' },
    },
};

const STRATEGY_OPTIONS = [
    { label: 'Hold (default)',    value: GetSimulationStrategyEnum.Hold          },
    { label: 'Take Profit +5%',   value: GetSimulationStrategyEnum.Tp5           },
    { label: 'Take Profit +10%',  value: GetSimulationStrategyEnum.Tp10          },
    { label: 'Take Profit +20%',  value: GetSimulationStrategyEnum.Tp20          },
    { label: 'Take Profit +30%',  value: GetSimulationStrategyEnum.Tp30          },
    { label: 'Trailing Stop 10%', value: GetSimulationStrategyEnum.TrailingStop  },
    { label: 'TP10 then Hold',    value: GetSimulationStrategyEnum.Tp10Hold      },
    { label: 'Trail Switch',      value: GetSimulationStrategyEnum.TrailSwitch   },
];

export const AlphaPicksPage: React.FC = () => {
    useDocumentTitle('Alpha Picks');

    const [picks, setPicks]             = useState<AlphaPickSummary[]>([]);
    const [activePick, setActivePick]   = useState<AlphaPickSummary | null>(null);
    const [dailyBars, setDailyBars]     = useState<AlphaPickBarDto[]>([]);
    const [simulation, setSimulation]   = useState<SimulationResponse | null>(null);
    const [fromPickOrder, setFromPick]  = useState(1);
    const [entryType, setEntryType]     = useState<GetSimulationEntryTypeEnum>(GetSimulationEntryTypeEnum.Publish);
    const [strategy, setStrategy]       = useState<GetSimulationStrategyEnum>(GetSimulationStrategyEnum.Hold);
    const [loading, setLoading]         = useState(true);
    const [syncLoading, setSyncLoading] = useState(false);

    const chart1Ref = useRef<HTMLDivElement>(null);
    const chart2Ref = useRef<HTMLDivElement>(null);
    const chart1Api = useRef<IChartApi | null>(null);
    const chart2Api = useRef<IChartApi | null>(null);

    // Load portfolio on mount
    useEffect(() => {
        alphaPicksApi.getPortfolio().then(res => {
            const sorted = [...(res.data ?? [])].sort((a, b) => (a.pickOrder ?? 0) - (b.pickOrder ?? 0));
            setPicks(sorted);
            const current = sorted.filter(p => p.status === 'CURRENT').at(-1) ?? sorted.at(-1) ?? null;
            setActivePick(current);
        }).catch(console.error).finally(() => setLoading(false));
    }, []);

    // Load daily bars when active pick or entry type changes
    useEffect(() => {
        if (!activePick?.ticker || !activePick?.publishedAt) return;
        alphaPicksApi.getDailyBars(
            activePick.ticker,
            activePick.publishedAt,
            entryType as unknown as GetDailyBarsEntryTypeEnum,
        )
            .then(res => setDailyBars(res.data ?? []))
            .catch(console.error);
    }, [activePick, entryType]);

    // Load simulation when fromPickOrder, entryType, or strategy changes
    useEffect(() => {
        alphaPicksApi.getSimulation(fromPickOrder, entryType, strategy)
            .then(res => setSimulation(res.data ?? null))
            .catch(console.error);
    }, [fromPickOrder, entryType, strategy]);

    // Graph 1 — candlestick % chart
    useEffect(() => {
        if (!chart1Ref.current || dailyBars.length === 0) return;
        chart1Api.current?.remove();
        const chart = createChart(chart1Ref.current, {
            ...CHART_LAYOUT,
            width: chart1Ref.current.clientWidth,
            height: 280,
            timeScale: { timeVisible: false },
        });
        chart1Api.current = chart;
        const series = chart.addSeries(CandlestickSeries, {
            upColor:        '#4caf50',
            downColor:      '#f44336',
            borderUpColor:  '#4caf50',
            borderDownColor:'#f44336',
            wickUpColor:    '#4caf50',
            wickDownColor:  '#f44336',
        });
        const data = dailyBars
            .filter(b => b.openPct != null && b.timestamp)
            .map(b => ({
                time:  b.timestamp as Time,
                open:  b.openPct!,
                high:  b.highPct!,
                low:   b.lowPct!,
                close: b.closePct!,
            }));
        series.setData(data);
        chart.timeScale().fitContent();
        return () => { chart1Api.current?.remove(); chart1Api.current = null; };
    }, [dailyBars]);

    // Graph 2 — area simulation chart
    useEffect(() => {
        if (!chart2Ref.current || !simulation?.dataPoints?.length) return;
        chart2Api.current?.remove();
        const chart = createChart(chart2Ref.current, {
            ...CHART_LAYOUT,
            width: chart2Ref.current.clientWidth,
            height: 240,
            timeScale: { timeVisible: false },
        });
        chart2Api.current = chart;
        const areaSeries = chart.addSeries(AreaSeries, {
            lineColor:   '#7986cb',
            topColor:    'rgba(63,81,181,0.4)',
            bottomColor: 'rgba(63,81,181,0.02)',
        });
        // Deduplicate by date: multiple picks on same day → keep last (highest pick order)
        const uniquePoints = Array.from(
            simulation.dataPoints
                .filter(p => p.date != null && p.portfolioValue != null)
                .reduce((m, p) => { m.set(p.date!, p); return m; }, new Map())
                .values()
        ).sort((a, b) => a.date!.localeCompare(b.date!));
        areaSeries.setData(
            uniquePoints.map(p => ({ time: p.date as Time, value: p.portfolioValue! }))
        );
        chart.timeScale().fitContent();
        return () => { chart2Api.current?.remove(); chart2Api.current = null; };
    }, [simulation, loading]);

    const handleSync = useCallback(async () => {
        setSyncLoading(true);
        try { await alphaPicksApi.triggerSync(); } catch (e) { console.error(e); } finally { setSyncLoading(false); }
    }, []);

    if (loading) return <Spinner />;

    const currentValue = simulation?.currentValue?.toFixed(2) ?? '—';
    const totalReturn  = simulation?.totalReturnPct?.toFixed(1) ?? '—';
    const activeIdx    = picks.findIndex(p => p.pickOrder === activePick?.pickOrder);
    const strategyLabel = STRATEGY_OPTIONS.find(o => o.value === strategy)?.label ?? strategy;
    const entryLabel    = entryType === GetSimulationEntryTypeEnum.Minute2 ? 'Min-2 entry' : 'Publish entry';

    return (
        <ContentLayout header={
            <Header variant="h1" actions={
                <Button loading={syncLoading} onClick={handleSync}>Sync Now</Button>
            }>Alpha Picks Performance</Header>
        }>
            <SpaceBetween size="l">

                {/* Entry type toggle — affects both graphs */}
                <Container>
                    <SpaceBetween size="s">
                        <Box variant="awsui-key-label">Entry Price</Box>
                        <SegmentedControl
                            selectedId={entryType}
                            onChange={({ detail }) => setEntryType(detail.selectedId as GetSimulationEntryTypeEnum)}
                            options={[
                                { id: GetSimulationEntryTypeEnum.Publish, text: 'Publish price' },
                                { id: GetSimulationEntryTypeEnum.Minute2, text: '+1 min price' },
                            ]}
                        />
                        <Box variant="small" color="text-body-secondary">
                            Publish: entry at the SA article publish timestamp.
                            &nbsp;+1 min: entry at the first minute-bar open after the publish timestamp.
                            Falls back to publish price if +1 min data is not yet backfilled.
                        </Box>
                    </SpaceBetween>
                </Container>

                {/* Pick strip */}
                <Container>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {picks.map(p => (
                            <button
                                key={p.pickOrder}
                                onClick={() => setActivePick(p)}
                                style={{
                                    padding: '4px 10px', borderRadius: 4, border: 'none', cursor: 'pointer',
                                    background: activePick?.pickOrder === p.pickOrder ? '#1b5e20' : '#1a237e',
                                    color: activePick?.pickOrder === p.pickOrder ? '#69f0ae' : '#82b1ff',
                                    fontWeight: 'bold', fontSize: 12,
                                }}
                            >
                                {p.ticker} #{p.pickOrder}
                            </button>
                        ))}
                    </div>
                </Container>

                {/* Graph 1 */}
                <Container header={
                    <Header
                        variant="h2"
                        description={`Daily high/low/close as % change from ${entryLabel}. Resets to 0% at each pick.`}
                        actions={
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button
                                    disabled={activeIdx <= 0}
                                    onClick={() => setActivePick(picks[activeIdx - 1])}
                                >
                                    Prev
                                </Button>
                                <Box><b>{activePick?.ticker}</b> #{activePick?.pickOrder}</Box>
                                <Button
                                    disabled={activeIdx >= picks.length - 1}
                                    onClick={() => setActivePick(picks[activeIdx + 1])}
                                >
                                    Next
                                </Button>
                            </SpaceBetween>
                        }
                    >
                        Individual Pick Performance — {activePick?.ticker}
                    </Header>
                }>
                    <div ref={chart1Ref} style={{ width: '100%' }} />
                    {activePick && (
                        <Box variant="small" color="text-body-secondary" padding={{ top: 's' }}>
                            Pick: {activePick.publishedAt?.substring(0, 16).replace('T', ' ')} ET &nbsp;&middot;&nbsp;
                            Price at pick: ${activePick.priceAtPublish?.toFixed(2) ?? '—'} &nbsp;&middot;&nbsp;
                            +1 min: ${activePick.priceAt1MinAfterPublish?.toFixed(2) ?? '—'} &nbsp;&middot;&nbsp;
                            Sell: {activePick.sellPrice != null ? `$${activePick.sellPrice.toFixed(2)}` : 'Active'}
                        </Box>
                    )}
                </Container>

                {/* Graph 2 */}
                <Container header={
                    <Header
                        variant="h2"
                        description={`$1,000 compounding · ${entryLabel} · ${strategyLabel} · Value: $${currentValue} (${Number(totalReturn) >= 0 ? '+' : ''}${totalReturn}%)`}
                        actions={
                            <SpaceBetween direction="horizontal" size="xs">
                                <Select
                                    selectedOption={
                                        STRATEGY_OPTIONS.find(o => o.value === strategy) ??
                                        { label: strategy, value: strategy }
                                    }
                                    onChange={({ detail }) =>
                                        setStrategy(detail.selectedOption.value as GetSimulationStrategyEnum)
                                    }
                                    options={STRATEGY_OPTIONS}
                                />
                                <Select
                                    selectedOption={{ label: `From pick #${fromPickOrder}`, value: String(fromPickOrder) }}
                                    onChange={({ detail }) => setFromPick(Number(detail.selectedOption.value))}
                                    options={picks.map(p => ({
                                        label: `#${p.pickOrder} ${p.ticker} — ${p.publishedAt?.substring(0, 7) ?? ''}`,
                                        value: String(p.pickOrder),
                                    }))}
                                />
                            </SpaceBetween>
                        }
                    >
                        Cumulative Portfolio Simulation
                    </Header>
                }>
                    <div ref={chart2Ref} style={{ width: '100%' }} />
                </Container>

            </SpaceBetween>
        </ContentLayout>
    );
};
